import { ParaglideMessage } from '@inlang/paraglide-js-react';
import type { Endpoint } from '../../features/catalog/model';
import { fmtUtcDay } from '../../lib/format';
import { BrandIconChip } from '../../components/BrandIcon';
import { BrandMark } from '../../components/BrandMark';
import { Band } from '../../components/editorial/Band';
import { Tile } from '../../components/editorial/Tile';
import { Eyebrow } from '../../components/Eyebrow';
import { FieldCard } from './FieldCard';
import { ResponseCard } from './ResponseCard';
import { referenceRows } from './referenceRows';
import { SellerSchemaLink } from './SellerSchemaLink';
import { m } from '../../paraglide/messages';

export interface EndpointReferenceProps {
  /** catalog platform id, for the response card's logo */
  apiId: string;
  endpoint: Endpoint;
  /** RFC3339 instant the gateway last recomputed the health rollup. */
  verifiedAt?: string;
  /** the rollup's reporting window, e.g. `30d`. */
  healthWindow?: string;
  /** the SKU's request JSON Schema, when the build projected one */
  inputSchema?: unknown;
  /** the SKU's response JSON Schema, when the build projected one */
  outputSchema?: unknown;
}

/** The band's anchor, for the links that send a reader to every response field. */
export const REFERENCE_ID = 'reference';

/**
 * The house `.tile-art` is a fixed-height, inert picture. Here it holds the
 * real contract, so it grows with its card, takes clicks and lets text be
 * selected; the pastel field still fills it edge to edge.
 */
const ART = 'flex-1 h-auto pointer-events-auto select-text p-4 s520:p-6 lg:p-8';

/** The house card shadow on a white card that already has its hairline. */
const FLOAT = 'w-full rounded-14 shadow-[var(--card-shadow)]';

/**
 * The static half of a public endpoint page: the request and the response as
 * two editorial tiles in the homepage pattern, each a pastel field holding a
 * white field card, with the date we last measured. Everything here renders on
 * the server, which is the whole point - the interactive console above it
 * carries the same facts and contributes none of them to the served HTML.
 *
 * The verified date is the gateway's own `lastComputedAt` from `/benchmarks`,
 * already resolved by the route loader. Nothing here is generated at render
 * time, so a prerendered page states when we last measured rather than when it
 * happens to be read.
 */
export const EndpointReference = ({
  apiId,
  endpoint,
  verifiedAt,
  healthWindow,
  inputSchema,
  outputSchema,
}: EndpointReferenceProps) => {
  // One string, not a JSX expression list: React separates adjacent text nodes
  // with `<!-- -->` markers, and the reader this line exists for is an
  // extractor lifting a sentence out of the markup.
  const day = fmtUtcDay(verifiedAt);
  const verified = !day
    ? ''
    : healthWindow
      ? m.endpoint_reference_verified_window({ day, window: healthWindow })
      : m.endpoint_reference_verified({ day });
  const rows = referenceRows(endpoint, inputSchema, outputSchema);

  return (
    <Band id={REFERENCE_ID} wash="wash">
      <div className="band">
        <Eyebrow>{m.endpoint_reference_eyebrow()}</Eyebrow>
        <h2 className="display-3 mt-4 text-text">
          <ParaglideMessage
            message={m.endpoint_reference_heading}
            markup={{ muted: ({ children }) => <span className="muted-word">{children}</span> }}
          />
        </h2>
        <p className="lede mt-3 max-w-150">{m.endpoint_reference_lede()}</p>
        {verified ? <p className="mono mt-3 text-11 text-text-3">{verified}</p> : null}
        {/* The tiles stretch to one height, so the short request card sits
            centred in its field. */}
        <div className="mt-10 grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-6 [&>figure]:flex [&>figure]:flex-col">
          <Tile
            lead={m.endpoint_reference_send_lead()}
            pastel="mint"
            artClassName={`${ART} flex items-center`}
            art={
              <div className={FLOAT}>
                <FieldCard
                  title={m.endpoint_reference_request_title()}
                  caption={m.endpoint_reference_request_caption()}
                  mark={
                    <span className="grid size-9.5 place-items-center rounded-10 bg-accent-soft">
                      <BrandMark size={20} />
                    </span>
                  }
                  rows={rows.request}
                />
              </div>
            }
          >
            {m.endpoint_reference_send_caption()}
          </Tile>
          <Tile
            lead={m.endpoint_reference_get_lead()}
            pastel="lilac"
            artClassName={ART}
            art={
              <div className={FLOAT}>
                <ResponseCard
                  endpoint={endpoint}
                  outputSchema={outputSchema}
                  title={m.endpoint_reference_response_title()}
                  caption={m.endpoint_reference_response_caption()}
                  mark={<BrandIconChip id={apiId} size={38} iconSize={19} />}
                  rows={rows.response}
                  requirable={rows.requirable}
                />
              </div>
            }
          >
            {m.endpoint_reference_get_caption()}
          </Tile>
        </div>
        <SellerSchemaLink sku={endpoint.sku} />
      </div>
    </Band>
  );
};
